import { FileUploadStatus } from '@app/shared/models/dr-file';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { FileCategory, FileType, FileTypes } from '@app/shared/models/file-type';
import { Input, Directive } from '@angular/core';
import { FileUtilityService } from '@app/core/services/file-utility.service';
import { FileActions } from '@app/shared/models/file-actions';
import { BaseComponent } from '@app/shared/components/base/base.component';
import { BaseModel } from '@app/shared/models/base';

export interface Thumbnailable {
    thumbnailLink: string;
    thumbnailFileName: string;
    thumbnailStatus: FileUploadStatus;
    fileType: FileType;
}

@Directive()
export class ThumbNailBaseComponent<T extends BaseModel & Thumbnailable> extends BaseComponent {

    @Input()
    public file: T;

    @Input()
    public disabledOptions: FileActions[];

    @Input()
    public label?: string;

    @Input()
    public dynamicDeleteBtnFn?: (file: T) => boolean;

    public rendered: boolean;

    get fileStatus() {
        return FileUploadStatus;
    }

    get fileType() {
        return FileTypes;
    }

    get fileCategory() {
        return FileCategory;
    }

    get canDownload(): boolean {
        return this.file.thumbnailStatus === FileUploadStatus.READY &&
            (!this.disabledOptions || this.disabledOptions.indexOf(FileActions.DOWNLOAD) === -1);
    }

    get canDelete(): boolean {
        return !this.disabledOptions || this.disabledOptions.indexOf(FileActions.DELETE) === -1;
    }

    get canEdit(): boolean {
        return this.file.thumbnailStatus === FileUploadStatus.READY &&
            (!this.disabledOptions || this.disabledOptions.indexOf(FileActions.EDIT) === -1);
    }

    get canView(): boolean {
        return this.file.thumbnailStatus === FileUploadStatus.READY &&
            (!this.disabledOptions || this.disabledOptions.indexOf(FileActions.VIEW) === -1);
    }

    get hasTooltip(): boolean {
        return !this.disabledOptions || this.disabledOptions.indexOf(FileActions.TOOLTIP_ON_HOVER) === -1;
    }

    get hasLabel(): boolean {
        return !this.disabledOptions || this.disabledOptions.indexOf(FileActions.LABEL) === -1;
    }

    constructor(protected fileUtilityService: FileUtilityService,
                protected translateService: TranslateService) {
        super();
    }

    public getReducedSizeFileLinkWithToken(): string {
        const version = (<any>this.file).version;
        return `${this.file.thumbnailLink}${this.fileUtilityService.addToken()}&size=320x200&version=${version}`;
    }

    public getFileTitle(): string {
        const displayName = this.file.thumbnailFileName;
        if (this.file.thumbnailStatus === FileUploadStatus.ERROR) {
            return `${this.translateService.instant(marker('error.file_upload'))}: ${displayName}`;
        } else if (this.hasTooltip && this.file.thumbnailStatus === FileUploadStatus.READY) {
            return `${displayName}${this.label ? ' - ' + this.label : ''}`;
        } else {
            return '';
        }
    }

    public loaded(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.rendered = true;
            });
        });
    }

    public canDeleteDynamically(): boolean {
        return !this.dynamicDeleteBtnFn || this.dynamicDeleteBtnFn(this.file);
    }

}
